import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import Link from '@eliadmin/core/src/components/link';
import { CATEGORY_CONFIG } from '@eliadmin/core/src/constants/category-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import type { PropsWithChildren, ReactNode } from 'react';

export type CategoryCardProps = {
  href?: string;
  noLink?: boolean;
  className?: { root?: string; content?: string };
};

export function CategoryCard({
  href,
  noLink = false,
  className,
  ...props
}: PropsWithChildren<CategoryCardProps>) {
  if (noLink) {
    return (
      <Card
        asChild
        className={cn('h-[56px] hover:bg-secondary/80', 'ea-category-card', className?.root)}
        variant="outlined"
      >
        <CardContent
          className={cn(
            'line-clamp-2 flex h-full w-full flex-row items-center gap-4 text-ellipsis p-3',
            'ea-category-card__content',
            className?.content,
          )}
        >
          {props.children}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      asChild
      // TODO: fix secondary colors
      className={cn('hover:bg-secondary/80', 'ea-category-card', className?.root)}
      variant="secondary"
    >
      <Link href={href || '#'} className={cn('w-full', 'ea-category-card__link', className?.root)}>
        <CardContent
          className={cn(
            'line-clamp-2 flex h-12 w-full flex-row items-center gap-4 p-3 sm:h-16',
            'ea-category-card__content',
            className?.content,
          )}
        >
          {props.children}
        </CardContent>
      </Link>
    </Card>
  );
}

type CategoryCardImageProps = {
  url: string;
  alt?: string;
  className?: {
    root?: string;
    image?: string;
  };
};
export function CategoryCardImage(props: CategoryCardImageProps) {
  return (
    <div className={cn('', 'ea-category-card-image__root', props.className?.root)}>
      <Image
        alt={resolveImageAlt(props, 'category-image')}
        src={props.url}
        width="0"
        height="0"
        sizes="160px"
        className={cn('h-8 w-8', 'ea-category-card-image__image', props.className?.image)}
      />
    </div>
  );
}

export function CategoryCardName(props: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn('w-full text-left text-black', 'ea-category-card-name', props.className)}>
      <span>{props.children}</span>
    </div>
  );
}

export function CategoryCardDescription(props: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn('pt-4 text-left text-xs', 'ea-category-card-description', props.className)}>
      <span>{props.children}</span>
    </div>
  );
}

export function CategoryCardArrow(props: { className?: string; icon?: ReactNode }) {
  return (
    <div
      className={cn(
        'flex flex-1 justify-end text-left text-accent',
        'ea-category-card-arrow',
        props.className,
      )}
    >
      {props.icon || CATEGORY_CONFIG.categoryCard.arrowIcon}
    </div>
  );
}
